// The queue behind the celebration overlay.
//
// Two prizes can land on one turn — a stage boundary is exactly the moment a
// king dies, and a king always leaves something behind — so this is a queue
// rather than a flag. They are shown one at a time, in the order they
// happened, and each is dismissed by being taken.
//
// **Nothing is stored in game state.** What has already been shown is a set of
// ids in a ref: the state says what just happened, this says what has been
// looked at, and the second is not something a replay or another client should
// ever see. A "seen" flag in the log would be a command that changes nothing
// about the game.

import { useEffect, useRef, useState } from "react";

import type { Command, GameState } from "src/engine/rules/commands";
import { type Celebration, celebrationsFor, sample } from "src/engine/session/celebrations";

export function useCelebrations(
  state: GameState,
  log: readonly Command[],
  /** Off outside single player: there are no cards and no stages to clear. */
  enabled: boolean,
  /**
   * Announce a cleared front.
   *
   * Off wherever the branch picker is on (PLAN §19b): that panel *is* the
   * front's card — it reports the same tally, from the same `statsOf`, and it
   * comes up before the camp lands rather than after. Both would be two modals
   * back to back, the second telling the player what they had just decided.
   *
   * An argument rather than a guess from the state, because "is there a picker"
   * is a fact about the shell, not about the game.
   */
  stages = true
): {
  current: Celebration | undefined;
  remaining: number;
  claim(): void;
  /** Show one without earning it. The dev menu, and nothing else. */
  preview(kind: "stage"): void;
} {
  const [queue, setQueue] = useState<Celebration[]>([]);
  // Ids already queued. A ref, not state: adding to it must not itself cause a
  // render, or the effect below re-runs on its own output.
  const seen = useRef(new Set<string>());

  useEffect(() => {
    if (!enabled) return;
    const fresh = celebrationsFor(state, log).filter(
      (one) => !seen.current.has(one.id) && (stages || one.kind !== "stage")
    );
    if (fresh.length === 0) return;
    for (const one of fresh) seen.current.add(one.id);
    setQueue((was) => [...was, ...fresh]);
  }, [state, log, enabled, stages]);

  return {
    current: queue[0],
    remaining: Math.max(0, queue.length - 1),
    claim: () => setQueue((was) => was.slice(1)),
    // Not a command: a celebration is derived from what a turn reported, and
    // pushing a fake one through the reducer would put a lie in the log — the
    // log is the save format, the replay and the network protocol.
    preview: (kind) =>
      setQueue((was) => [...was, sample(kind, `preview:${kind}:${was.length}:${Date.now()}`)])
  };
}
